// src/hooks/useEmployeeAuth.js
import { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { resolveTenantId } from '../utils/tenant';

export const useEmployeeAuth = () => {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true); // Start with true to handle initial localStorage check
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if employee is logged in from localStorage
    const savedEmployee = localStorage.getItem('loggedInEmployee');
    if (savedEmployee) {
      try {
        const parsed = JSON.parse(savedEmployee);
        setEmployee(parsed);
      } catch (err) {
        localStorage.removeItem('loggedInEmployee');
      }
    }
    setLoading(false); // Done checking localStorage
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
      try {
        // normalize input email
        const normalizedEmail = (email || '').trim().toLowerCase();

        // Find employee by email in tenant-scoped employees collection
        const tenantId = await resolveTenantId();
        const employeesRef = collection(db, `tenants/${tenantId}/employees`);
        const q = query(employeesRef, where('email', '==', normalizedEmail));
        const querySnapshot = await getDocs(q);
      
      // if email-based lookup fails, attempt UID-based fallback (doc id == auth uid)
      let employeeDoc = null;
      if (querySnapshot.empty) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const fallbackRef = doc(db, `tenants/${tenantId}/employees`, auth?.currentUser?.uid || '');
          const fallbackSnap = await getDoc(fallbackRef);
          if (fallbackSnap.exists()) {
            employeeDoc = fallbackSnap;
          } else {
            throw new Error('No employee found with this email address');
          }
        } catch (fallbackErr) {
          throw fallbackErr;
        }
      } else {
        employeeDoc = querySnapshot.docs[0];
      }
      const employeeData = employeeDoc.data();
      
      // Passwords should be managed by Firebase Auth, not stored in Firestore.
      // The tenant's employees collection should include the employee uid that maps to an Auth user.
      const employment = employeeData.employment || {};
      const storedPassword = employment.password;

      if (storedPassword) {
        // If we see a stored password, we will not use it for auth. Log and treat this as legacy data.
        console.warn('Legacy plaintext password present in employee doc; please migrate to Auth for employee:', employeeDoc.id);
      }

      // Require the employee doc to include a uid mapping to the Auth user.
      if (!employeeData.uid) {
        throw new Error('Employee record missing uid mapping. Please ensure employees are associated with Auth users.');
      }

      // For authentication, rely on Firebase Auth signInWithEmailAndPassword (handled elsewhere).
      // Here we only return the employee record; actual credential verification should be done against Auth.
      
      // Check if employee is active (case-insensitive)
      const currentStatus = (employment.currentStatus || '').toString();
      if (currentStatus && currentStatus.toLowerCase() !== 'active') {
        throw new Error('Employee account is not active');
      }
      
      const employeeInfo = {
        id: employeeDoc.id,
        ...employeeData,
        isFirstLogin: !storedPassword || storedPassword === 'Welcome1'
      };
      
      setEmployee(employeeInfo);
      localStorage.setItem('loggedInEmployee', JSON.stringify(employeeInfo));
      
      // Trigger theme reload for user-specific preferences
      if (window.reloadUserTheme) {
        window.reloadUserTheme();
      }
      
      return employeeInfo;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

    const changePassword = async (newPassword) => {
    // Employee password changes should be performed via Firebase Auth flows (sendPasswordResetEmail / updatePassword).
    throw new Error('Change password for employees must be done via Firebase Auth. Use the password reset / change flow.');
  };

  const logout = () => {
    setEmployee(null);
    localStorage.removeItem('loggedInEmployee');
    
    // Trigger theme reset to light mode
    if (window.reloadUserTheme) {
      window.reloadUserTheme();
    }
  };

  return {
    employee,
    loading,
    error,
    login,
    logout,
    changePassword,
    isAuthenticated: !!employee
  };
};
